import React, { useState } from 'react';
import IconButtonEx, { IconButtonExProps } from "../components/IconButtonEx";
import PublishIcon from '@mui/icons-material/Publish';
import { Box, Button, DialogContent, LinearProgress, Stack, Typography, Stepper, Step, StepLabel, useTheme } from '@mui/material';
import { OngoingAssessmentErrors } from '../apiClients/OngoingAssessmentClient';
import { DevelopmentItem, ImprovementPlan, OngoingAssessment } from '../models/dtos';
import ConditionalDisplay from '../components/ConditionalDisplay';
import TalentEventManagementComponent from '../ui-components/TalentEventManagementComponent';
import AssessmentClient, { AssessmentErrors, AssessmentPublishContext } from '../apiClients/AssessmentClient';
import TalentClient, { DevelopmentPlanItemFilter, TalentErrors } from '../apiClients/TalentClient';
import { ExtractForSetError, IsBetween, sleep } from '../Helpers';
import ImprovementPlanSelectionComponent from '../ui-components/ImprovementPlanSelectionComponent';
import DevelopmentPlanDisplayComponent, { FinalStepDevPlanItem } from '../ui-components/DevelopmentPlanDisplayComponent';
import DevelopmentItemSelectionComponent from '../ui-components/DevelopmentItemSelectionComponent';
import { useAccessInfo } from '../contexts/AccessTokenProvider';
import { useSnackBarDisplay } from '../components/SnackBarDisplay';
import OngoingAssessmentComponent from '../ui-components/OngoingAssessmentComponent';
import { useWindowDimensions } from '../components/WindowDimensions';
import BusySpinner from '../components/BusySpinner';
import DevelopmentClient, { CreateDevelopmentItem } from '../apiClients/DevelopmentClient';
import { ResponsiveDialog, ResponsiveDialogHeader, StickyDialogActions } from '../components/responsive';
import useViewportMode from '../hooks/useViewportMode';


export interface HeaderDisplay {
    talentName: string;
    templateName: string;
    assessmentScore: string;
    adjustedScore?: string;
}

export interface StepperStep {
    number: number,
    label: string
}
export type AssessmentPublishStep = 'Review' | 'Events' | 'Development' | 'Improvement' | 'Publish';

export interface OngoingAssessmentPublishPopupProps extends Omit<IconButtonExProps, 'onClick'> {
    talentId: string;
    icon?: JSX.Element;
    onClose?: () => Promise<void> | void;
    onPublished?: () => Promise<void> | void;
    onError: (message, subtitle?: string, isValidation?: boolean) => Promise<void> | void;
}

const OngoingAssessmentPublishPopup = ({ talentId, icon = <PublishIcon fontSize='small' />, onClose = () => { }, onPublished = () => { }, onError, ...btnProps }: OngoingAssessmentPublishPopupProps) => {

    const [busy, setBusy] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);
    const theme = useTheme();
    const { accessInfo } = useAccessInfo();
    const { height } = useWindowDimensions();
    const viewport = useViewportMode();

    const [SnackBarDisplay, setMessage] = useSnackBarDisplay();

    const DevPlanMin = accessInfo.selectedOrganizationSettings.DevelopmentPlanMin;
    const DevPlanMax = accessInfo.selectedOrganizationSettings.DevelopmentPlanMax;

    const [isNextDisabled, setNextDisabled] = useState<boolean>(false);

    const [displayHeader, setDisplayHeader] = useState<HeaderDisplay>(null);
    const [assessment, setAssessment] = useState<OngoingAssessment>(null);
    const [improvementPlan, setImprovementPlan] = useState<ImprovementPlan>(null);
    const [developmentItems, setDevelopmentItems] = useState<DevelopmentItem[]>(null);
    const [developmentPlan, setDevelopmentPlan] = useState<FinalStepDevPlanItem[]>([]);
    const [hasDevelopmentItems, setHasDevelopmentItems] = useState<boolean>(false);
    const [selectedDevelopmentIds, setSelectedDevelopmentIds] = useState<string[]>([]);
    const [selectedImprovementIds, setSelectedImprovementIds] = useState<string[]>([]);
    const [startedDevelopmentPlan, setStartedDevelopmentPlan] = useState<boolean>(false);
    const [developmentPlanItemsLeftToSave, setDevelopmentPlanItemsLeftToSave] = useState<CreateDevelopmentItem[]>();

    const [showAssessment, setShowAssessment] = useState<boolean>(true);
    const [showIncidents, setShowIncidents] = useState<boolean>(false);
    const [showImprovementPlan, setShowImprovementPlan] = useState<boolean>(false);
    const [showLastStep, setShowLastStep] = useState<boolean>(false);
    const [showAdjustedScore, setShowAdjustedScore] = useState<boolean>(false);
    const [showDevelopmentItems, setShowDevelopmentItems] = useState<boolean>(false);
    const [instructions] = useState<string>('');

    const [steps, setSteps] = useState<StepperStep[]>([]);
    const [activeStep, setActiveStep] = useState<number>(0);
    const loadGeneration = React.useRef(0);

    React.useEffect(() => () => {
        loadGeneration.current += 1;
    }, []);

    const title = 'Publish Ongoing Assessment';
    // const initialInstructions = `Select ${DevPlanMin} to ${DevPlanMax} development and improvement items combined.`;

    // it's possible to have no improvement items if there are the minumum number of ad-hoc development items 
    const thereAreImprovementItems = (): boolean => selectedImprovementIds.length > 0;

    const loadData = async (generation: number) => {

        const isCurrentLoad = () => generation === loadGeneration.current;

        try {
            // Validate Ongoing Assessment
            let apiResponseValidate = await new AssessmentClient().Validate(talentId);
            if (!isCurrentLoad())
                return;
            if (apiResponseValidate.Content.IsNotValid) {
                let message = apiResponseValidate.Content.ValidationErrors.join("<br />");
                handleOnError(message, OngoingAssessmentErrors.Validate, true);
                return;
            }

            // The actual ongoingAssessment graph object
            const apiResponse = await new TalentClient().GetOngoingAssessment(talentId);
            if (!isCurrentLoad())
                return;
            if (ExtractForSetError(apiResponse, OngoingAssessmentErrors.GetSingle, handleOnError))
                return;

            let assessment = apiResponse.Content;
            setAssessment(assessment);

            // Set Header
            let header = {
                talentName: `${assessment.Talent.FirstName} ${assessment.Talent.LastName}`,
                templateName: assessment.TemplateName,
                assessmentScore: assessment.ScoreCard.OverallScore.toString(),
                adjustedScore: assessment.ScoreCard.AdjustedScore.toString()
            }
            setDisplayHeader(header);

            // Get Development Items
            const developmentResponse = await new DevelopmentClient().GetDevelopmentItems(talentId, DevelopmentPlanItemFilter.Active);
            if (!isCurrentLoad())
                return;
            if (ExtractForSetError(developmentResponse, OngoingAssessmentErrors.GetSingle, handleOnError))
                return;

            const di = developmentResponse.Content;
            setDevelopmentItems(di);

            const hasDevelopmentItems = di.length > 0;
            setHasDevelopmentItems(hasDevelopmentItems);

            const steps = buildSteps(di.length > 0);
            setSteps(steps);

            // Build Improvement Plan
            const improvementPlanResponse = await new DevelopmentClient().BuildImprovementPlan(talentId);
            if (!isCurrentLoad())
                return;
            if (ExtractForSetError(improvementPlanResponse, OngoingAssessmentErrors.GetSingle, handleOnError))
                return;

            setImprovementPlan(improvementPlanResponse.Content);

            // setInstructions(initialInstructions);

        } catch (error) {
            if (isCurrentLoad())
                handleOnError(error, OngoingAssessmentErrors.GetSingle);
        }
        finally {
            if (isCurrentLoad())
                setBusy(false);
        }
    };

    const stepperSteps: Record<AssessmentPublishStep, StepperStep> = {
        Review: { number: 0, label: 'Review Assessment' },
        Events: { number: 1, label: 'Events' },
        Development: { number: 2, label: 'Development Plan Items' },
        Improvement: { number: 3, label: 'Improvement Items' },
        Publish: { number: 4, label: 'Finalize and Publish' }
    }

    const buildSteps = (hasDevelopmentItems: boolean): StepperStep[] => {
        if (hasDevelopmentItems) {
            return [
                stepperSteps['Review'],
                stepperSteps['Events'],
                stepperSteps['Development'],
                stepperSteps['Improvement'],
                stepperSteps['Publish']
            ];
        } else {
            return [
                stepperSteps['Review'],
                stepperSteps['Events'],
                stepperSteps['Improvement'],
                stepperSteps['Publish']
            ];
        }
    };

    const handleNext = async () => {
        if (showAssessment) {
            setShowAssessment(false);
            setShowIncidents(true);
            setShowAdjustedScore(assessment.ScoreCard.PerformanceItems.length > 0);

            setActiveStep(stepperSteps['Events'].number);
        }
        else if (!showAssessment && showIncidents) {
            setShowIncidents(false);

            if (hasDevelopmentItems) {
                setShowDevelopmentItems(true);
                setActiveStep(stepperSteps['Development'].number);
                setNextDisabled(true);
            }
            else {
                setShowImprovementPlan(true);
                setActiveStep(stepperSteps['Improvement'].number);
            }

        }
        else if (!showAssessment && !showIncidents && showDevelopmentItems) {

            if (isDevelopmentPlanNextEnabled(selectedDevelopmentIds)) {
                setShowDevelopmentItems(false);
                setShowImprovementPlan(true);
                setNextDisabled(isImprovementPlanNextDisabled());
                setActiveStep(stepperSteps['Improvement'].number);
            }
        }
        else if (!showAssessment && !showIncidents && !showDevelopmentItems && showImprovementPlan) {
            const isValid = validateMinDevelopmentPlanItemsSelected();

            if (isValid) {
                setShowImprovementPlan(false);
                setShowLastStep(true);

                if (thereAreImprovementItems()) {
                    buildDevelopmentPlan();
                    setNextDisabled(true);
                } else {
                    setNextDisabled(false);
                }

                setActiveStep(stepperSteps['Publish'].number);
            }
        }
        else if (!showAssessment && !showIncidents && !showDevelopmentItems && !showImprovementPlan && showLastStep) {
            const published = await submit();
            if (published) {
                try {
                    await onPublished();
                } finally {
                    // Publication is already committed. Always leave the wizard even if a parent refresh fails.
                    handleClose();
                }
            }
        }
    };

    const handlePrevious = () => {
        setNextDisabled(false);

        if (showLastStep) {
            setShowLastStep(false);
            setShowImprovementPlan(true);
            setActiveStep(stepperSteps['Improvement'].number);
        }
        else if (showIncidents) {
            setShowIncidents(false);
            setShowAssessment(true);
            setShowAdjustedScore(false);
            setActiveStep(stepperSteps['Review'].number);
        }
        else if (showDevelopmentItems) {
            setShowIncidents(true);
            setShowDevelopmentItems(false);
            setActiveStep(stepperSteps['Events'].number);
        }
        else if (showImprovementPlan) {
            setShowImprovementPlan(false);

            if (hasDevelopmentItems) {
                setShowDevelopmentItems(true);
                setActiveStep(stepperSteps['Development'].number);
            }
            else {
                setShowIncidents(true);
                setActiveStep(stepperSteps['Events'].number);
            }

        }
    };

    const submit = async (): Promise<boolean> => {
        try {
            setBusy(true);

            const context: AssessmentPublishContext = {
                TalentId: assessment.Talent.Id,
                DevelopmentPlanItems: developmentPlanItemsLeftToSave
            };

            var apiResponse = await new AssessmentClient().Publish(context);
            if (ExtractForSetError(apiResponse, AssessmentErrors.Publish, handleOnError))
                return false;

            // set toast message
            setMessage('Assessment has been published!', false);
            await sleep(3000);
            return true;

        } catch (error) {
            handleOnError(error, OngoingAssessmentErrors.Publish);
            return false;
        }
        finally {
            setBusy(false);
        }
    };

    const resetPublishState = () => {
        setNextDisabled(false);
        setShowAssessment(true);
        setShowIncidents(false);
        setShowDevelopmentItems(false);
        setShowImprovementPlan(false);
        setShowLastStep(false);
        setShowAdjustedScore(false);
        setAssessment(null);
        setDisplayHeader(null);
        setDevelopmentItems(null);
        setImprovementPlan(null);
        setHasDevelopmentItems(false);
        setSteps([]);
        setActiveStep(0);
        setSelectedDevelopmentIds([]);
        setSelectedImprovementIds([]);
        setDevelopmentPlan([]);
        setStartedDevelopmentPlan(false);
        setDevelopmentPlanItemsLeftToSave(undefined);
    };

    const handleOpen = async () => {
        const generation = loadGeneration.current + 1;
        loadGeneration.current = generation;
        resetPublishState();
        setBusy(true);
        setOpen(true);
        await loadData(generation);
    };

    const handleClose = () => {
        loadGeneration.current += 1;
        setOpen(false);
        setBusy(false);
        resetPublishState();
        onClose();
    };

    const handleOnError = (message, subtitle, displayAsValidation?: boolean) => {
        handleClose();
        onError(message, subtitle, displayAsValidation);
    }

    // passing in ids because the setSelectedDevelopmentIds hasn't been updated from handleDevelopmentPlanItemSelectionChange yet
    const isDevelopmentPlanNextEnabled = (ids: string[]): boolean => {
        let allItemsHandled: boolean = true;

        for (let i = 0; i < developmentItems.length; i++) {
            const di = developmentItems[i];
            // rule: must be selected OR completed but cannot be both
            const isSelected = ids.includes(di.Id);
            const isCompleted = DevelopmentClient.RuleItemCompleted(di.CompleteDate);

            if (!isSelected && !isCompleted) {
                allItemsHandled = false;
                break;
            }
        };

        return allItemsHandled;
    }

    const isImprovementPlanNextDisabled = (): boolean => {
        const totalItems = selectedImprovementIds.length + selectedDevelopmentIds.length;
        const isNextDisabled = !IsBetween(totalItems, DevPlanMin, DevPlanMax, true);
        return isNextDisabled;
    }

    const validateMaxDevelopmentPlanItemsSelected = (totalDevelopmentItems: number, totalImprovementItems: number): void => {
        const totalItems = totalDevelopmentItems + totalImprovementItems;
        const isNextDisabled = totalItems > DevPlanMax;

        if (isNextDisabled) {
            setMessage(`Select fewer than ${DevPlanMax} development plan items`, true);
        }

        setNextDisabled(isNextDisabled);
    }

    const validateMinDevelopmentPlanItemsSelected = (): boolean => {
        const totalItems = selectedImprovementIds.length + selectedDevelopmentIds.length;
        const isNextDisabled = totalItems < DevPlanMin;

        if (isNextDisabled) {
            setMessage(`Select at least ${DevPlanMin} development plan items`, true);
        }

        setNextDisabled(isNextDisabled);
        return !isNextDisabled;
    }

    // const updateInstructions = (totalItemsSelected: number) => {
    //     // setInstructions(`Select ${DevPlanMin} to ${DevPlanMax} development and improvement items combined. You've selected ${totalItemsSelected} items.`);
    // }

    const handleDevelopmentPlanItemSelectionChange = (ids: string[]) => {
        setSelectedDevelopmentIds(ids);

        const isDevItemsNextEnabled = isDevelopmentPlanNextEnabled(ids);

        if (!isNextDisabled)
            setMessage(`Every development plan item must be selected or completed.`, true);

        setNextDisabled(!isDevItemsNextEnabled);

        if (isDevItemsNextEnabled)
            validateMaxDevelopmentPlanItemsSelected(ids.length, selectedImprovementIds.length);
    }

    const handleImprovementPlanItemSelectionChange = (ids: string[]) => {
        setSelectedImprovementIds(ids);
        validateMaxDevelopmentPlanItemsSelected(selectedDevelopmentIds.length, ids.length);
    }

    const buildDevelopmentPlan = async () => {
        try {
            setBusy(true);

            let selectedItems = improvementPlan.ImprovementItems.filter(f => selectedImprovementIds.includes(f.Proficiency.Id));
            let improvPlan: ImprovementPlan = {
                TalentId: talentId,
                ImprovementItems: selectedItems
            };

            const apiResponse = await new DevelopmentClient().BuildDevelopmentPlan(talentId, improvPlan);
            if (ExtractForSetError(apiResponse, "Error building development plan", handleOnError))
                return;

            // create the list of super dev items
            const finalStepDevItems = [];

            apiResponse.Content.DevelopmentPlanItems
                .forEach(x => {
                    if (x.Type === "Proficiency") {
                        var proficiencyId = x.AssociationId;
                        var prof = assessment.Proficiencies.find(f => f.ProficiencyId === proficiencyId);
                        var superDev: FinalStepDevPlanItem = {
                            Id: x.Id,
                            AssociationId: x.AssociationId,
                            Name: x.Name,
                            Narrative: x.Narrative,
                            NarrativePrompt: x.NarrativePrompt,
                            Type: x.Type,
                            proficiency: prof,
                        };
                        finalStepDevItems.push(superDev);
                    } else if (x.Type === "PII") {
                        var sdPII: FinalStepDevPlanItem = {
                            Id: x.Id,
                            AssociationId: x.AssociationId,
                            Name: x.Name,
                            Narrative: x.Narrative,
                            NarrativePrompt: x.NarrativePrompt,
                            Type: x.Type,
                            proficiency: null,
                        };
                        finalStepDevItems.push(sdPII);
                    }
                });

            setDevelopmentPlan(finalStepDevItems);

        } catch (error) {
            handleOnError(error, TalentErrors.GetSingle);
        }
        finally {
            setBusy(false);
        }
    }

    const handleReadyToSubmit = (itemsToCreate: CreateDevelopmentItem[]) => {
        setDevelopmentPlanItemsLeftToSave(itemsToCreate);
        setNextDisabled(false);
    }

    const currentStepIndex = Math.max(steps.findIndex(step => step.number === activeStep), 0);
    const currentStep = steps[currentStepIndex];
    const stepProgress = steps.length > 0 ? ((currentStepIndex + 1) / steps.length) * 100 : 0;
    const isPublishDataReady = displayHeader !== null
        && assessment !== null
        && developmentItems !== null
        && improvementPlan !== null
        && steps.length > 0;

    const assessmentHeader = viewport.isDesktop ? (
        <Stack direction='row'>
            <Typography sx={{ padding: theme.spacing(3) }}>{`${displayHeader?.talentName}`}</Typography>
            <Typography sx={{ padding: theme.spacing(3) }}>{`Template: ${displayHeader?.templateName}`}</Typography>
            <Typography sx={{ padding: theme.spacing(3) }}>{`Overall Score: ${displayHeader?.assessmentScore}`}</Typography>
            <ConditionalDisplay ifTrue={showAdjustedScore}>
                <Typography sx={{ padding: theme.spacing(3) }}>{`Adjusted Score: ${displayHeader?.adjustedScore}`}</Typography>
            </ConditionalDisplay>
        </Stack>
    ) : (
        <Stack spacing={0.5} sx={{ px: 2, pt: 1.5 }} aria-label="Assessment summary">
            <Typography variant="subtitle1" fontWeight={700}>{displayHeader?.talentName}</Typography>
            <Typography variant="body2" color="text.secondary">{displayHeader?.templateName}</Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap">
                <Typography variant="body2">Overall: {displayHeader?.assessmentScore}</Typography>
                {showAdjustedScore && <Typography variant="body2">Adjusted: {displayHeader?.adjustedScore}</Typography>}
            </Stack>
        </Stack>
    );

    const stepProgressDisplay = viewport.isDesktop ? (
        <Stepper sx={{ marginTop: '1em', marginBottom: '2em' }} activeStep={activeStep}>
            {steps.map((step) => (
                <Step key={step.number}>
                    <StepLabel>{step.label}</StepLabel>
                </Step>
            ))}
        </Stepper>
    ) : (
        <Box sx={{ px: 2, py: 1.5 }} aria-label={`Step ${currentStepIndex + 1} of ${steps.length}: ${currentStep?.label ?? ''}`}>
            <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ mb: 0.75 }}>
                <Typography variant="subtitle2">{currentStep?.label}</Typography>
                <Typography variant="caption" color="text.secondary">{currentStepIndex + 1} of {steps.length}</Typography>
            </Stack>
            <LinearProgress variant="determinate" value={stepProgress} aria-label="Publish progress" />
        </Box>
    );

    return (
        <>
            <IconButtonEx disabled={busy} hoverContent={title} size='small' onClick={handleOpen} {...btnProps}>{icon}</IconButtonEx>

            <ResponsiveDialog aria-label={title} open={open} fullWidth maxWidth='xl' tabletFullScreen="portrait">
                {!viewport.isDesktop && <ResponsiveDialogHeader title={title} onClose={handleClose} />}
                <DialogContent sx={{ p: viewport.isDesktop ? undefined : 0, minHeight: 0, overflowY: 'auto' }}>
                    <BusySpinner busy={busy && isPublishDataReady}>
                        <div style={{ height: viewport.isDesktop ? height - 200 : 'auto', width: '100%' }}>
                            {!isPublishDataReady ? (
                                <Stack
                                    role="status"
                                    aria-live="polite"
                                    aria-busy="true"
                                    aria-label="Loading assessment details"
                                    spacing={2}
                                    sx={{ maxWidth: 480, mx: 'auto', px: 3, py: 6 }}
                                >
                                    <Typography variant="h6">Loading assessment details…</Typography>
                                    <Typography color="text.secondary">Preparing the review summary and publication steps.</Typography>
                                    <LinearProgress aria-label="Loading publish assessment" />
                                </Stack>
                            ) : (
                                <>
                                    {assessmentHeader}
                                    {stepProgressDisplay}
                                    <ConditionalDisplay ifTrue={showAssessment}>
                                        <OngoingAssessmentComponent subscriptionActive={true} talentId={talentId} assessmentLayoutOption={true} showFooter={false} heightDetractor={400} />
                                    </ConditionalDisplay>
                                    <ConditionalDisplay ifTrue={showIncidents}>
                                        <TalentEventManagementComponent subscriptionActive={true} talentId={assessment.Talent.Id} />
                                    </ConditionalDisplay>
                                    <ConditionalDisplay ifTrue={showDevelopmentItems}>
                                        <DevelopmentItemSelectionComponent instructions={instructions} selectedIds={selectedDevelopmentIds} data={developmentItems} onSelectionChange={handleDevelopmentPlanItemSelectionChange} />
                                    </ConditionalDisplay>
                                    <ConditionalDisplay ifTrue={showImprovementPlan}>
                                        <ImprovementPlanSelectionComponent instructions={instructions} selectedIds={selectedImprovementIds} data={improvementPlan.ImprovementItems} onSelectionChange={handleImprovementPlanItemSelectionChange} />
                                    </ConditionalDisplay>
                                    <ConditionalDisplay ifTrue={showLastStep && selectedImprovementIds.length > 0}>
                                        <Stack spacing={3}>
                                            <DevelopmentPlanDisplayComponent developmentItems={developmentPlan} talentId={talentId} onStartDevelopmentPlan={() => setStartedDevelopmentPlan(true)} onReadyToSubmit={(items) => handleReadyToSubmit(items)} />
                                            {/* <Typography>Are you ready to commit this Assessment to {displayHeader.talentName}'s permanent record? (this action is irreversible!!)</Typography> */}
                                        </Stack>
                                    </ConditionalDisplay>
                                </>
                            )}
                        </div>
                    </BusySpinner>
                </DialogContent>
                <StickyDialogActions
                    phoneLayout="inline"
                    sx={viewport.isTablet ? { pb: 'calc(8px + env(safe-area-inset-bottom, 0px))' } : undefined}
                >
                    <Button onClick={handleClose} color="inherit" variant="text">Cancel</Button>
                    {isPublishDataReady && (
                        <>
                            <ConditionalDisplay ifTrue={!startedDevelopmentPlan}>
                                <Button
                                    onClick={handlePrevious}
                                    disabled={viewport.isDesktop ? showAssessment && startedDevelopmentPlan : showAssessment || startedDevelopmentPlan}
                                    color="secondary"
                                    variant="outlined"
                                >Previous</Button>
                            </ConditionalDisplay>
                            <SnackBarDisplay displayMs={3000} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
                            <Button onClick={handleNext} disabled={isNextDisabled} color="primary" variant="outlined">
                                {showLastStep ? (viewport.isDesktop ? 'Commit' : 'Publish') : 'Next'}
                            </Button>
                        </>
                    )}
                </StickyDialogActions>
            </ResponsiveDialog>
        </>
    )
}

export default OngoingAssessmentPublishPopup;
